import { useEffect, useMemo, useState } from "react";
import { createFileRoute, Link, useParams } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { ArrowLeft, Loader2, RotateCcw, Save, Send } from "lucide-react";
import {
  getEmailTemplate,
  saveEmailTemplate,
  resetEmailTemplate,
  previewEmailTemplate,
  sendEmailTemplateTest,
} from "@/lib/email-templates.functions";
import { findPlaceholders } from "@/lib/email-templates/placeholders";
import { useAdminSession } from "@/hooks/use-admin-session";
import { confirmDialog } from "@/lib/confirm-dialog";
import { EmailMarkdownEditor } from "@/components/admin/EmailMarkdownEditor";

export const Route = createFileRoute("/_admin/admin/emails/$key")({
  component: EmailTemplateEditor,
});

function EmailTemplateEditor() {
  const { key } = useParams({ from: "/_admin/admin/emails/$key" });
  const qc = useQueryClient();
  // Ticket AD3: Der Testversand bleibt Administratoren vorbehalten.
  const session = useAdminSession();

  const fetchTemplate = useServerFn(getEmailTemplate);
  const save = useServerFn(saveEmailTemplate);
  const reset = useServerFn(resetEmailTemplate);
  const preview = useServerFn(previewEmailTemplate);
  const sendTest = useServerFn(sendEmailTemplateTest);

  const { data, isLoading, error } = useQuery({
    queryKey: ["admin", "email-template", key],
    queryFn: () => fetchTemplate({ data: { key } }),
  });

  const [subject, setSubject] = useState("");
  const [bodyMd, setBodyMd] = useState("");
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (data && !dirty) {
      setSubject(data.subject);
      setBodyMd(data.bodyMd);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const missing = useMemo(() => {
    if (!data) return [] as string[];
    const hay = `${subject}\n${bodyMd}`;
    return data.placeholders
      .filter((p) => p.required)
      .map((p) => p.name)
      .filter((n) => !new RegExp(`\\{\\{\\s*${n}\\s*\\}\\}`).test(hay));
  }, [data, subject, bodyMd]);

  // Ticket Z15a: gleiche Erkennung wie serverseitig – ein unbekannter Platzhalter
  // verhindert Speichern und Testversand.
  const unknown = useMemo(() => {
    if (!data) return [] as string[];
    const allowed = new Set(data.placeholders.map((p) => p.name));
    return findPlaceholders(subject, bodyMd)
      .filter((f) => !allowed.has(f.name))
      .map((f) => f.name);
  }, [data, subject, bodyMd]);

  const allowedList = useMemo(
    () => (data ? data.placeholders.map((p) => `{{${p.name}}}`).join(", ") : ""),
    [data],
  );

  const previewQuery = useQuery({
    queryKey: ["admin", "email-template-preview", key, subject, bodyMd],
    queryFn: () => preview({ data: { key, subject, bodyMd } }),
    enabled: Boolean(data) && bodyMd.length > 0,
  });

  const saveMutation = useMutation({
    mutationFn: () => save({ data: { key, subject, bodyMd } }),
    onSuccess: () => {
      setDirty(false);
      qc.invalidateQueries({ queryKey: ["admin", "email-template", key] });
      qc.invalidateQueries({ queryKey: ["admin", "email-templates"] });
      toast.success("Vorlage gespeichert.");
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const resetMutation = useMutation({
    mutationFn: () => reset({ data: { key } }),
    onSuccess: (res: any) => {
      setSubject(res.subject);
      setBodyMd(res.bodyMd);
      setDirty(false);
      qc.invalidateQueries({ queryKey: ["admin", "email-template", key] });
      qc.invalidateQueries({ queryKey: ["admin", "email-templates"] });
      toast.success("Standardtext wiederhergestellt.");
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const testMutation = useMutation({
    mutationFn: () => sendTest({ data: { key, subject, bodyMd } }),
    onSuccess: (res: any) => toast.success(`Testmail an ${res.recipient} versendet.`),
    onError: (e: Error) => toast.error(e.message),
  });

  if (isLoading) {
    return (
      <div className="mx-auto flex max-w-5xl items-center gap-2 px-6 py-8 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> Vorlage wird geladen …
      </div>
    );
  }
  if (error || !data) {
    return (
      <div className="mx-auto max-w-5xl px-6 py-8 text-sm text-destructive">
        Vorlage konnte nicht geladen werden: {(error as Error)?.message ?? "unbekannt"}
      </div>
    );
  }

  const insertPlaceholder = (name: string) => {
    setBodyMd((v) => `${v}${v.endsWith("\n") || !v ? "" : " "}{{${name}}}`);
    setDirty(true);
  };

  return (
    <div className="mx-auto w-full max-w-6xl px-6 py-8">
      <Link
        to="/admin/emails"
        className="mb-4 inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" aria-hidden="true" /> Zurück zur Übersicht
      </Link>

      <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold text-foreground">{data.displayName}</h1>
          <p className="text-sm text-muted-foreground">{data.purpose}</p>
          {data.security && (
            <p className="mt-2 rounded-md border border-border bg-muted/50 px-3 py-2 text-xs text-muted-foreground">
              Sicherheitsrelevante Vorlage: Diese Mail steuert Anmeldung oder
              Passwortvergabe. Bitte Pflicht-Platzhalter unverändert lassen und
              keine zusätzlichen Links ergänzen.
            </p>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          {session.isAdmin && (
            <button
              type="button"
              onClick={() => testMutation.mutate()}
              disabled={testMutation.isPending || missing.length > 0 || unknown.length > 0}
              className="inline-flex items-center gap-2 rounded-md border border-border px-3 py-2 text-sm font-medium hover:bg-secondary disabled:opacity-50"
            >
              {testMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
              ) : (
                <Send className="h-4 w-4" aria-hidden="true" />
              )}
              Testmail an mich
            </button>
          )}
          <button
            type="button"
            onClick={async () => {
              if (
                await confirmDialog({
                  title: "Eigene Fassung verwerfen?",
                  description: "Der Standardtext der Vorlage wird wiederhergestellt.",
                  confirmLabel: "Zurücksetzen",
                })
              ) {
                resetMutation.mutate();
              }
            }}
            disabled={resetMutation.isPending || !data.customized}
            className="inline-flex items-center gap-2 rounded-md border border-border px-3 py-2 text-sm font-medium hover:bg-secondary disabled:opacity-50"
          >
            <RotateCcw className="h-4 w-4" aria-hidden="true" /> Zurücksetzen
          </button>
          <button
            type="button"
            onClick={() => saveMutation.mutate()}
            disabled={saveMutation.isPending || missing.length > 0 || unknown.length > 0}
            className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:opacity-90 disabled:opacity-50"
          >
            {saveMutation.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
            ) : (
              <Save className="h-4 w-4" aria-hidden="true" />
            )}
            Speichern
          </button>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <div className="space-y-4">
          <div>
            <label htmlFor="tpl-subject" className="mb-1 block text-sm font-medium">
              Betreff
            </label>
            <input
              id="tpl-subject"
              value={subject}
              onChange={(e) => {
                setSubject(e.target.value);
                setDirty(true);
              }}
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
            />
          </div>

          <div>
            <label htmlFor="tpl-body" className="mb-1 block text-sm font-medium">
              Inhalt (Markdown)
            </label>
            <EmailMarkdownEditor id="tpl-body" value={bodyMd} onChange={(value) => {
              setBodyMd(value);
              setDirty(true);
            }} rows={22} allowLinks={!data.security} buttonTargets={data.security ? data.placeholders.filter((p) => p.name.endsWith("_link")).map((p) => `{{${p.name}}}`) : undefined} />
            <p className="mt-2 text-xs text-muted-foreground">
              Formatierung: <code># Überschrift</code>, <code>**fett**</code>,{" "}
              <code>- Liste</code>, <code>---</code> für eine Trennlinie. Button:{" "}
              <code>:::button {"{{aktions_link}}"} | Beschriftung:::</code>. Code-Box:{" "}
              <code>:::code {"{{code}}"}:::</code>.
            </p>
          </div>

          <div className="rounded-md border border-border p-4">
            <p className="mb-2 text-sm font-medium">Verfügbare Platzhalter</p>
            <ul className="space-y-2">
              {data.placeholders.map((p) => (
                <li key={p.name} className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <code className="text-xs">{`{{${p.name}}}`}</code>
                    {p.required && (
                      <span className="ml-2 text-xs font-medium text-destructive">Pflicht</span>
                    )}
                    <p className="text-xs text-muted-foreground">{p.description}</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => insertPlaceholder(p.name)}
                    className="shrink-0 rounded border border-border px-2 py-1 text-xs hover:bg-secondary"
                  >
                    Einfügen
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {missing.length > 0 && (
            <p className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive">
              Pflicht-Platzhalter fehlen: {missing.map((m) => `{{${m}}}`).join(", ")}. Ohne sie
              kann die Vorlage nicht gespeichert oder getestet werden.
            </p>
          )}
          {unknown.length > 0 && (
            <p className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive">
              {unknown.length === 1 ? "Unbekannter Platzhalter" : "Unbekannte Platzhalter"}:{" "}
              {unknown.map((m) => `{{${m}}}`).join(", ")}. Zulässig sind für diese Vorlage:{" "}
              {allowedList || "keine"}. Solange ein unbekannter Platzhalter im Text steht, ist
              Speichern und Testversand gesperrt. Bitte auf Schreibweise achten: Platzhalter
              werden zeichengenau kleingeschrieben.
            </p>
          )}
        </div>

        <div>
          <p className="mb-2 text-sm font-medium">
            Live-Vorschau{" "}
            <span className="font-normal text-muted-foreground">
              (Werte aus dem jüngsten echten Vorgang)
            </span>
          </p>
          {previewQuery.data?.sourceNote && (
            <p className="mb-2 text-xs text-muted-foreground">{previewQuery.data.sourceNote}</p>
          )}
          <div className="rounded-md border border-border bg-muted/30 p-3 text-xs text-muted-foreground">
            Betreff: {previewQuery.data?.subject ?? subject}
          </div>

          <iframe
            title="E-Mail-Vorschau"
            srcDoc={previewQuery.data?.html ?? ""}
            sandbox=""
            className="mt-2 h-[720px] w-full rounded-md border border-border bg-white"
          />
        </div>
      </div>
    </div>
  );
}
